import { WebhookSignal, WebhookConfig, WebhookHistory, WebhookTestResult } from '../types/webhook';
import { signalGenerator } from './signalGenerator';

class WebhookService {
  private config: WebhookConfig;
  private history: WebhookHistory;
  private listeners: ((signal: WebhookSignal) => void)[] = [];

  constructor() {
    this.config = this.loadConfig();
    this.history = this.loadHistory();
    // Always start signal generation by default
    this.startWebhookServer();
  }

  private loadConfig(): WebhookConfig {
    try {
      const saved = localStorage.getItem('tradingview-webhook-config');
      if (saved) {
        return JSON.parse(saved);
      }
    } catch (e) {
      console.warn('Failed to load webhook config:', e);
    }
    
    return {
      enabled: true, // Enable by default
      endpoint: '',
      secret: '',
      autoUpdateCharts: true,
      showNotifications: true,
      filters: {
        symbols: ['BTCUSDT', 'ETHUSDT', 'SOLUSDT'], // Default symbols
        types: ['buy', 'sell', 'alert'],
        minConfidence: 0.3 // Lower threshold for more signals
      }
    };
  }

  private loadHistory(): WebhookHistory {
    try {
      const saved = localStorage.getItem('tradingview-webhook-history');
      if (saved) {
        const history = JSON.parse(saved);
        // Clean old signals when loading
        const oneHourAgo = Date.now() - (60 * 60 * 1000);
        history.signals = history.signals.filter((signal: WebhookSignal) => 
          signal.timestamp > oneHourAgo
        );
        return history;
      }
    } catch (e) {
      console.warn('Failed to load webhook history:', e);
    }
    
    return {
      signals: [],
      lastReceived: 0,
      totalReceived: 0
    };
  }

  private saveConfig(): void {
    try {
      localStorage.setItem('tradingview-webhook-config', JSON.stringify(this.config));
    } catch (e) {
      console.warn('Failed to save webhook config:', e);
    }
  }

  private saveHistory(): void {
    try {
      localStorage.setItem('tradingview-webhook-history', JSON.stringify(this.history));
    } catch (e) {
      console.warn('Failed to save webhook history:', e);
    }
  }

  private cleanOldSignals(): void {
    const oneHourAgo = Date.now() - (60 * 60 * 1000);
    const initialCount = this.history.signals.length;
    
    this.history.signals = this.history.signals.filter(signal => 
      signal.timestamp > oneHourAgo
    );
    
    const removedCount = initialCount - this.history.signals.length;
    if (removedCount > 0) {
      console.log(`Cleaned up ${removedCount} old signals (older than 1 hour)`);
      this.saveHistory();
    }
  }

  private startWebhookServer(): void {
    // Always start signal generation (no config check needed)
    signalGenerator.startSignalGeneration(30000); // Generate signals every 30 seconds
    
    // Also poll for webhook data
    this.pollForWebhooks();
  }

  private pollForWebhooks(): void {
    // Always poll for signals (no config check needed)
    // Check for new webhook data every 2 seconds
    setInterval(() => {
      this.checkForNewSignals();
    }, 2000);
    
    // Clean up old signals every 5 minutes
    setInterval(() => {
      this.cleanOldSignals();
    }, 5 * 60 * 1000);
  }

  private checkForNewSignals(): void {
    try {
      const webhookData = localStorage.getItem('tradingview-webhook-data');
      if (!webhookData) return;

      const signals: WebhookSignal[] = JSON.parse(webhookData);
      const newSignals = signals.filter(signal => 
        signal.timestamp > this.history.lastReceived
      );

      if (newSignals.length > 0) {
        this.processSignals(newSignals);
      }
    } catch (e) {
      console.warn('Failed to check for webhook signals:', e);
    }
  }

  private processSignals(signals: WebhookSignal[]): void {
    signals.forEach(signal => {
      // Apply filters
      if (!this.passesFilters(signal)) return;

      // Add to history
      this.history.signals.push(signal);
      this.history.lastReceived = Math.max(this.history.lastReceived, signal.timestamp);
      this.history.totalReceived++;

      // Notify listeners
      this.listeners.forEach(listener => listener(signal));

      // Show notification if enabled
      if (this.config.showNotifications) {
        this.showNotification(signal);
      }
    });

    // Keep signals for up to 1 hour (3600000 ms)
    const oneHourAgo = Date.now() - (60 * 60 * 1000);
    this.history.signals = this.history.signals.filter(signal => 
      signal.timestamp > oneHourAgo
    );

    this.saveHistory();
  }

  private passesFilters(signal: WebhookSignal): boolean {
    const { filters } = this.config;

    // Check symbol filter
    if (filters.symbols.length > 0 && !filters.symbols.includes(signal.symbol)) {
      return false;
    }

    // Check type filter
    if (filters.types.length > 0 && !filters.types.includes(signal.type)) {
      return false;
    }

    // Check confidence filter
    if (filters.minConfidence && signal.confidence && signal.confidence < filters.minConfidence) {
      return false;
    }

    return true;
  }

  private showNotification(signal: WebhookSignal): void {
    const message = signal.message || `${signal.type.toUpperCase()} signal for ${signal.symbol}`;
    
    if ('Notification' in window && Notification.permission === 'granted') {
      new Notification('Trading Signal', {
        body: message,
        icon: '/favicon.ico'
      });
    } else if (Notification.permission !== 'denied') {
      Notification.requestPermission().then(permission => {
        if (permission === 'granted') {
          new Notification('Trading Signal', {
            body: message,
            icon: '/favicon.ico'
          });
        }
      });
    }
  }

  // Public API
  public getConfig(): WebhookConfig {
    return { ...this.config };
  }

  public updateConfig(updates: Partial<WebhookConfig>): void {
    this.config = { ...this.config, ...updates };
    this.saveConfig();
    
    if (updates.enabled !== undefined) {
      if (updates.enabled) {
        this.startWebhookServer();
      } else {
        signalGenerator.stopSignalGeneration();
      }
    }
  }

  public getHistory(): WebhookHistory {
    return { ...this.history };
  }

  public clearHistory(): void {
    this.history = {
      signals: [],
      lastReceived: 0,
      totalReceived: 0
    };
    this.saveHistory();
  }

  public addListener(listener: (signal: WebhookSignal) => void): () => void {
    this.listeners.push(listener);
    return () => {
      const index = this.listeners.indexOf(listener);
      if (index > -1) {
        this.listeners.splice(index, 1);
      }
    };
  }

  public async testWebhook(): Promise<WebhookTestResult> {
    try {
      // Generate a mock signal for testing
      const testSignal = signalGenerator.generateMockSignal();
      testSignal.id = `test-${Date.now()}`;
      testSignal.message = 'Test webhook signal - ' + testSignal.message;

      // Store test signal
      const existingData = localStorage.getItem('tradingview-webhook-data');
      const signals = existingData ? JSON.parse(existingData) : [];
      signals.push(testSignal);
      localStorage.setItem('tradingview-webhook-data', JSON.stringify(signals));

      return {
        success: true,
        message: `Test signal sent: ${testSignal.type.toUpperCase()} for ${testSignal.symbol}`,
        timestamp: Date.now()
      };
    } catch (e) {
      return {
        success: false,
        message: `Test failed: ${e}`,
        timestamp: Date.now()
      };
    }
  }

  public clearSignalHistory(): void {
    this.history = {
      signals: [],
      lastReceived: 0,
      totalReceived: 0
    };
    this.saveHistory();
    console.log('Signal history cleared');
  }

  public async fetchRealSignals(): Promise<WebhookTestResult> {
    try {
      const signals = await signalGenerator.fetchCoinGeckoSignals();
      
      if (signals.length > 0) {
        // Store real signals
        const existingData = localStorage.getItem('tradingview-webhook-data');
        const existingSignals = existingData ? JSON.parse(existingData) : [];
        existingSignals.push(...signals);
        localStorage.setItem('tradingview-webhook-data', JSON.stringify(existingSignals));
        
        return {
          success: true,
          message: `Fetched ${signals.length} real signals from CoinGecko`,
          timestamp: Date.now()
        };
      } else {
        return {
          success: false,
          message: 'No significant price movements detected',
          timestamp: Date.now()
        };
      }
    } catch (e) {
      return {
        success: false,
        message: `Failed to fetch real signals: ${e}`,
        timestamp: Date.now()
      };
    }
  }

  public generateWebhookUrl(): string {
    // In a real implementation, this would generate a proper webhook URL
    // For now, we'll return a placeholder
    return `${window.location.origin}/webhook/${this.generateId()}`;
  }

  private generateId(): string {
    return Math.random().toString(36).substr(2, 9);
  }
}

export const webhookService = new WebhookService();
